import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useConfirm, confirmLogout } from '../../components/ConfirmDialog';
import { ToastContainer } from 'react-toastify';
import { HomeIcon, UsersIcon, UserGroupIcon, OfficeBuildingIcon, DocumentTextIcon, DocumentReportIcon, CogIcon, XIcon, MenuIcon, ShieldCheckIcon, BookOpenIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon, LogoutIcon } from '@heroicons/react/outline';
import Footer from "../../components/Footer";
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from "react";
import { hasRole, ROLES } from "../../shared/Utils";

import { ssoProviders } from "../../utils/sso";
import { ShieldExclamationIcon, SparklesIcon } from "@heroicons/react/outline";
import WhatsNewDialog, { stepsFor, hasSeenWhatsNew, markWhatsNewSeen } from "../../components/WhatsNew";
import { bindActionCreators } from "redux";
import { onLogoutRequested, refreshSessionRequested } from '../../actions/authAction';
import { stopImpersonation } from '../../actions/impersonateAction';
import { connect } from "react-redux";

// Pages only cell leaders can use; others are sent back to the dashboard home.
const LEADER_ONLY_PATHS = ['/dashboard/my-cells', '/dashboard/cellmembers', '/dashboard/guests', '/dashboard/reports'];

const Dashboard = ({ user, onLogoutRequested: logoutUser, stopImpersonation: exitImpersonation, refreshSessionRequested: refreshSession, impersonatedUser, isImpersonating }) => {
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Desktop sidebar can collapse to icons only (remembered on this device).
    const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === '1');
    const [tooltip, setTooltip] = useState(null); // { label, top }
    const toggleCollapsed = () => {
        setIsCollapsed((current) => {
            localStorage.setItem('sidebarCollapsed', current ? '0' : '1');
            return !current;
        });
        setTooltip(null);
    };
    // Fixed-position tooltip so the scrollable nav doesn't clip it.
    const tooltipHandlers = (label) => (isCollapsed ? {
        onMouseEnter: (e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setTooltip({ label, top: rect.top + rect.height / 2, left: rect.right + 8 });
        },
        onFocus: (e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setTooltip({ label, top: rect.top + rect.height / 2, left: rect.right + 8 });
        },
        onMouseLeave: () => setTooltip(null),
        onBlur: () => setTooltip(null),
    } : {});

    const confirm = useConfirm();
    const location = useLocation();
    const activeUser = isImpersonating ? impersonatedUser : user;

    // The stored session is a snapshot from sign-in; refresh it on load so
    // roles and led cells changed elsewhere (or by deleting a cell) show up.
    useEffect(() => {
        if (user?.id && refreshSession) refreshSession();
    }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Leave leader-only pages once the user no longer leads any cell.
    const onLeaderPage = LEADER_ONLY_PATHS.some((p) => location.pathname.startsWith(p));
    const lostLeaderAccess = Boolean(user?.id) && onLeaderPage && !hasRole(activeUser, ROLES.CELL_LEADER);
    useEffect(() => {
        if (lostLeaderAccess) navigate('/dashboard/', { replace: true });
    }, [lostLeaderAccess]); // eslint-disable-line react-hooks/exhaustive-deps
    const leaderCells = useMemo(() => activeUser?.cells || [], [activeUser]);
    const cellStorageKey = activeUser?.id ? `activeCellId:${activeUser.id}` : null;

    // Leaders can lead several cells; remember which one they're working on.
    const [selectedCellId, setSelectedCellId] = useState(null);
    useEffect(() => {
        const stored = cellStorageKey ? Number(localStorage.getItem(cellStorageKey)) : null;
        setSelectedCellId(stored || null);
    }, [cellStorageKey]);

    const activeCell = leaderCells.find((c) => c.id === selectedCellId) || leaderCells[0] || null;

    const handleSelectCell = (event) => {
        const id = Number(event.target.value);
        setSelectedCellId(id);
        if (cellStorageKey) localStorage.setItem(cellStorageKey, String(id));
    };

    // "What's new" walkthrough: opens once per user per release (not while
    // viewing as someone else), and from the header button any time.
    const whatsNewSteps = useMemo(() => stepsFor(user, { ssoEnabled: ssoProviders().length > 0 }), [user]);
    const [whatsNewOpen, setWhatsNewOpen] = useState(false);
    const [whatsNewSeen, setWhatsNewSeen] = useState(() => (user?.id ? hasSeenWhatsNew(user.id) : true));
    useEffect(() => {
        if (user?.id && !isImpersonating && !hasSeenWhatsNew(user.id) && whatsNewSteps.length > 1) {
            setWhatsNewOpen(true);
        }
    }, [user?.id, isImpersonating, whatsNewSteps.length]);
    const closeWhatsNew = () => {
        setWhatsNewOpen(false);
        if (user?.id) markWhatsNewSeen(user.id);
        setWhatsNewSeen(true);
    };
    const whatsNewNavigate = (to) => {
        closeWhatsNew();
        navigate(to);
    };

    // Dismissible for this browser session.
    const [ssoNudgeDismissed, setSsoNudgeDismissed] = useState(() => sessionStorage.getItem('ssoNudgeDismissed') === '1');

    if (!user || !user.id) {
        navigate('/login');
        return null;
    }

    const name = user?.name;

    // Nudge password users to link Google/Apple.
    const showSsoNudge = !isImpersonating
        && !ssoNudgeDismissed
        && ssoProviders().length > 0
        && !(user?.linkedProviders && user.linkedProviders.length > 0)
        && (!user?.authMethod || user.authMethod === 'password');
    const dismissSsoNudge = () => {
        sessionStorage.setItem('ssoNudgeDismissed', '1');
        setSsoNudgeDismissed(true);
    };

    const logout = async () => {
        if (!(await confirmLogout(confirm))) return;
        setIsMobileMenuOpen(false);
        logoutUser();
    };

    const navItems = [
        { to: '/dashboard/', label: 'Home', icon: HomeIcon, roles: [1, 2, 3], end: true },
        { to: '/dashboard/my-cells', label: 'My Cells', icon: OfficeBuildingIcon, roles: [1] },
        { to: '/dashboard/cells', label: 'Cells', icon: OfficeBuildingIcon, roles: [2] },
        { to: '/dashboard/cellmembers', label: 'Cell Members', icon: UserGroupIcon, roles: [1] },
        { to: '/dashboard/guests', label: 'Guests', icon: UsersIcon, roles: [1] },
        { to: '/dashboard/reports', label: 'Reports', icon: DocumentReportIcon, roles: [1] },
        { to: '/dashboard/users', label: 'Cell Leaders', icon: UsersIcon, roles: [2] },
        { to: '/dashboard/admins', label: 'Admins', icon: ShieldCheckIcon, roles: [3] },
        { to: '/dashboard/admin/reports', label: 'Admin Reports', icon: DocumentTextIcon, roles: [2] },
        { to: '/dashboard/verse', label: 'Verse of the Day', icon: BookOpenIcon, roles: [1, 2, 3] },
        { to: '/dashboard/settings', label: 'Settings', icon: CogIcon, roles: [1, 2, 3] },
    ];

    const visibleNavItems = navItems.filter((item) => hasRole(activeUser, ...item.roles));

    const navLinkClassName = (collapsed) => ({ isActive }) => (
        `group flex items-center rounded-lg py-2.5 text-sm font-medium transition-all duration-200 ${
            collapsed ? 'justify-center px-0' : 'gap-3 px-3'
        } ${
            isActive
                ? 'bg-emerald-50 text-emerald-700 shadow-sm'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }`
    );

    const renderNavLinks = (onClick, collapsed = false) => (
        visibleNavItems.map((item) => {
            const Icon = item.icon;
            return (
                <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={navLinkClassName(collapsed)}
                    onClick={(e) => { setTooltip(null); if (onClick) onClick(e); }}
                    aria-label={collapsed ? item.label : undefined}
                    {...(collapsed ? tooltipHandlers(item.label) : {})}
                >
                    <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                    {!collapsed && <span>{item.label}</span>}
                </NavLink>
            );
        })
    );

    return (
        <div className="min-h-screen bg-slate-100">
            <ToastContainer />
            <WhatsNewDialog open={whatsNewOpen} onClose={closeWhatsNew} steps={whatsNewSteps} onNavigate={whatsNewNavigate} />
            <div className="flex min-h-screen">
                <aside
                    className={`hidden lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col lg:border-r lg:border-gray-200 lg:bg-white lg:transition-[width] lg:duration-200 ${
                        isCollapsed ? 'lg:w-20' : 'lg:w-80'
                    }`}
                >
                    <div className={`flex h-screen min-h-0 flex-col py-6 ${isCollapsed ? 'px-3' : 'px-6'}`}>
                        <div className={`mb-8 flex items-start ${isCollapsed ? 'flex-col items-center gap-4' : 'justify-between'}`}>
                            {isCollapsed ? (
                                <p className="text-xs font-bold uppercase tracking-wide text-emerald-600" aria-label="ANFGC Cell Report">CR</p>
                            ) : (
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-600">ANFGC</p>
                                    <h1 className="mt-2 text-2xl font-semibold text-gray-900">Cell Report</h1>
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={toggleCollapsed}
                                aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                                aria-expanded={!isCollapsed}
                                className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                                {...tooltipHandlers('Expand sidebar')}
                            >
                                {isCollapsed
                                    ? <ChevronDoubleRightIcon className="h-5 w-5" aria-hidden="true" />
                                    : <ChevronDoubleLeftIcon className="h-5 w-5" aria-hidden="true" />}
                            </button>
                        </div>

                        <div className={`min-h-0 flex-1 overflow-y-auto ${isCollapsed ? '' : 'pr-1'}`}>
                            <nav className="space-y-2" aria-label="Main">
                                {renderNavLinks(undefined, isCollapsed)}
                            </nav>
                        </div>

                        <div className="mt-4 shrink-0 border-t border-gray-100 pt-4">
                            <button
                                className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-3 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-red-700"
                                onClick={logout}
                                aria-label={isCollapsed ? 'Logout' : undefined}
                                {...tooltipHandlers('Logout')}
                            >
                                {isCollapsed ? <LogoutIcon className="h-5 w-5" aria-hidden="true" /> : 'Logout'}
                            </button>
                        </div>
                    </div>
                </aside>

                {isCollapsed && tooltip && (
                    <div
                        role="tooltip"
                        className="pointer-events-none fixed z-50 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg lg:block"
                        style={{ top: tooltip.top, left: tooltip.left }}
                    >
                        {tooltip.label}
                    </div>
                )}

                <div className="flex min-h-screen min-w-0 flex-1 flex-col">
                    <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/80 backdrop-blur">
                        <div className="flex h-16 w-full items-center justify-between px-4 sm:px-6 lg:px-8">
                            <button
                                type="button"
                                className="inline-flex items-center justify-center rounded-md p-2 text-gray-600 hover:bg-gray-100 lg:hidden"
                                onClick={() => setIsMobileMenuOpen(true)}
                            >
                                <MenuIcon className="h-6 w-6" />
                            </button>

                            <div className="hidden lg:block">
                                <p className="text-sm text-gray-500">Dashboard</p>
                            </div>

                            <div className="flex items-center gap-3">
                                {leaderCells.length > 1 && activeCell && (
                                    <label className="flex items-center gap-2 text-sm text-gray-600">
                                        <span className="hidden sm:inline">Cell:</span>
                                        <select
                                            aria-label="Current cell"
                                            value={activeCell.id}
                                            onChange={handleSelectCell}
                                            className="rounded-md border-gray-300 py-1 text-sm font-medium text-gray-800 shadow-sm focus:border-emerald-300 focus:ring focus:ring-emerald-200 focus:ring-opacity-50"
                                        >
                                            {leaderCells.map((cell) => (
                                                <option key={cell.id} value={cell.id}>{cell.name}</option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setWhatsNewOpen(true)}
                                    className="relative inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
                                >
                                    <SparklesIcon className="h-5 w-5" aria-hidden="true" />
                                    <span className="hidden sm:inline">What's new</span>
                                    <span className="sr-only sm:hidden">What's new</span>
                                    {!whatsNewSeen && (
                                        <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-rose-500" aria-label="New" />
                                    )}
                                </button>
                                <div className="rounded-full bg-gray-100 px-4 py-1.5 text-sm font-medium text-gray-700">
                                    {name}
                                </div>
                            </div>
                        </div>
                    </header>

                    {showSsoNudge && (
                        <div className="flex flex-col gap-2 border-b border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
                            <span className="flex items-start gap-2">
                                <ShieldExclamationIcon className="mt-0.5 h-5 w-5 shrink-0" />
                                <span>
                                    <strong>Secure your account:</strong> link Google or Apple so you can sign in without a password.
                                    Password sign-in will be retired soon.
                                </span>
                            </span>
                            <span className="flex shrink-0 items-center gap-3">
                                <NavLink
                                    to="/dashboard/settings?tab=signin"
                                    onClick={dismissSsoNudge}
                                    className="rounded-md bg-emerald-600 px-3 py-1.5 font-semibold text-white hover:bg-emerald-700"
                                >
                                    Link now
                                </NavLink>
                                <button type="button" onClick={dismissSsoNudge} className="font-medium text-emerald-800 hover:text-emerald-950">
                                    Later
                                </button>
                            </span>
                        </div>
                    )}

                    {isImpersonating && (
                        <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between sm:px-6 lg:px-8">
                            <span className="text-sm font-medium">
                                Viewing as <strong>{impersonatedUser?.name}</strong>
                            </span>
                            <button
                                onClick={() => exitImpersonation()}
                                className="bg-white text-amber-600 font-semibold text-xs px-3 py-1 rounded-full hover:bg-amber-50 transition-colors"
                            >
                                Exit
                            </button>
                        </div>
                    )}

                    <main className="flex-1">
                        <section className="w-full px-4 pb-8 pt-4 sm:px-6 lg:px-8">
                            <Outlet context={{ activeUser: isImpersonating ? impersonatedUser : null, activeCell }} />
                        </section>
                    </main>

                    <Footer />
                </div>
            </div>

            {isMobileMenuOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={() => setIsMobileMenuOpen(false)}
                    />
                    <aside className="absolute left-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl">
                        <div className="flex h-full min-h-0 flex-col px-5 py-5">
                            <div className="mb-6 flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-gray-900">Cell Report</h2>
                                <button
                                    type="button"
                                    className="rounded-md p-2 text-gray-600 hover:bg-gray-100"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    <XIcon className="h-5 w-5" />
                                </button>
                            </div>

                            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                                <nav className="space-y-2">
                                    {renderNavLinks(() => setIsMobileMenuOpen(false))}
                                </nav>
                            </div>

                            <div className="mt-4 shrink-0 border-t border-gray-100 pt-4">
                                <button
                                    className="flex w-full items-center justify-center rounded-lg bg-red-600 px-3 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-red-700"
                                    onClick={logout}
                                >
                                    Logout
                                </button>
                            </div>
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
}

Dashboard.propTypes = {
    user: PropTypes.object.isRequired,
}

Dashboard.defaultProps = {
    user: {}
}

const mapStateToProps = (state) => ({
    impersonatedUser: state.impersonate.impersonatedUser,
    isImpersonating: state.impersonate.isImpersonating,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
    onLogoutRequested,
    stopImpersonation,
    refreshSessionRequested,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
